<template>
  <div class="callback-search-container">
    <!-- 搜索表单 -->
    <el-form :model="searchForm" ref="searchFormRef" inline class="compact-form">
      <!-- 日期选择器 -->
      <el-form-item label="日期" prop="date" class="compact-item">
        <el-date-picker
          v-model="searchForm.date"
          type="date"
          placeholder="选择日期"
          format="yyyy年MM月dd日"
          value-format="yyyyMMdd"
          :picker-options="pickerOptions"
          :clearable="false"
          style="width: 200px"
        ></el-date-picker>
      </el-form-item>
      
      <!-- 方式选择 -->
      <el-form-item label="方式" prop="method" class="compact-item">
        <el-select 
          v-model="searchForm.method" 
          placeholder="选择方式"
          style="width: 100px"
        >
          <el-option label="最近" value="recent"></el-option>
          <el-option label="随机" value="random"></el-option>
        </el-select>
      </el-form-item>
      
      <!-- 数量选择 -->
      <el-form-item label="数量" prop="count" class="compact-item">
        <el-select 
          v-model="searchForm.count" 
          placeholder="选择数量"
          style="width: 100px"
        >
          <el-option label="10条" :value="10"></el-option>
          <el-option label="50条" :value="50"></el-option>
          <el-option label="100条" :value="100"></el-option>
        </el-select>
      </el-form-item>
      
      <!-- 回调状态选择 -->
      <el-form-item label="状态" prop="callbackStatus" class="compact-item">
        <el-select 
          v-model="searchForm.callbackStatus" 
          placeholder="选择状态"
          style="width: 100px"
        >
          <el-option label="不限" value="all"></el-option>
          <el-option label="有回调" value="has"></el-option>
          <el-option label="无回调" value="none"></el-option>
        </el-select>
      </el-form-item>
      
      <!-- 产品输入 -->
      <el-form-item label="产品" prop="product" class="compact-item">
        <el-input 
          v-model="searchForm.product" 
          placeholder="产品名"
          style="width: 200px"
          :disabled="!!externalProduct"
        ></el-input>
      </el-form-item>
      
      <!-- 渠道输入 -->
      <el-form-item label="渠道" prop="channel" class="compact-item">
        <el-input 
          v-model="searchForm.channel" 
          :disabled="!!externalChannel" 
          :placeholder="externalChannel ? '固定渠道' : '渠道'"
          style="width: 200px"
        >
        <template v-if="externalChannel" #append>{{ externalChannel }}</template>
        </el-input>
      </el-form-item>
      
      <!-- 搜索按钮 -->
      <el-form-item class="compact-item">
        <el-button type="primary" @click="handleSearch" size="small">搜索</el-button>
        <el-button @click="resetSearch" size="small">重置</el-button>
      </el-form-item>
    </el-form>
    
    <!-- 数据表格 -->
    <el-table :data="tableData" border stripe style="width: 100%" v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" sortable></el-table-column>
      <el-table-column prop="src" label="渠道" width="120"></el-table-column>
      <el-table-column prop="target" label="产品名" width="150"></el-table-column>
      <el-table-column prop="timestamp" label="上报时间" width="180"></el-table-column>
      
      <!-- 来源链接列 -->
      <el-table-column label="来源链接" min-width="180">
        <template #default="{row}">
          <div class="text-cell">
            <el-tooltip 
              :content="row.srcRequstUrl" 
              placement="top" 
              :disabled="!row.srcRequstUrl"
              popper-class="multi-poper"
            >
              <div class="text-content">
                {{ row.srcRequstUrl }}
              </div>
            </el-tooltip>
            <el-button 
              size="mini" 
              icon="el-icon-document-copy"
              class="copy-btn"
              @click="copyToClipboard(row.srcRequstUrl)"
            ></el-button>
          </div>
        </template>
      </el-table-column>
          
      <!-- 上报链接列 -->
      <el-table-column label="上报链接" min-width="180">
        <template #default="{row}">
          <div class="text-cell">
            <el-tooltip 
              :content="row.targetRequestUrl" 
              placement="top" 
              :disabled="!row.targetRequestUrl"
              popper-class="multi-poper"
            >
              <div class="text-content">
                {{ row.targetRequestUrl }}
              </div>
            </el-tooltip>
            <el-button 
              size="mini" 
              icon="el-icon-document-copy"
              class="copy-btn"
              @click="copyToClipboard(row.targetRequestUrl)"
            ></el-button>
          </div>
        </template>
      </el-table-column>

      <!-- 上报结果 -->
      <el-table-column label="上报结果" min-width="180">
        <template #default="{row}">
          <div class="text-cell">
            <el-tooltip 
              :content="row.targetRsp" 
              placement="top" 
              :disabled="!row.targetRsp"
              popper-class="multi-poper"
            >
              <div class="text-content">
                {{ row.targetRsp }}
              </div>
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      
      <!-- 回调详情 -->
      <el-table-column label="回调详情" width="120">
        <template #default="{row}">
          <el-button 
            size="mini" 
            @click="showCallbackDetail(row)"
          >
            查看详情
          </el-button>
        </template>
      </el-table-column>
      
      <!-- 操作按钮 -->
      <el-table-column label="操作" width="100">
        <template #default="{row}">
          <el-button 
            type="primary" 
            size="mini" 
            @click="triggerCallback(row)"
          >
            回调
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页控件 -->
    <el-pagination
      class="pagination"
      :current-page="pagination.currentPage"
      :page-sizes="[10, 20, 50, 100]"
      :page-size="pagination.pageSize"
      :total="pagination.total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
    ></el-pagination>
    
    <!-- 回调详情弹窗 -->
    <el-dialog
      title="回调详情"
      :visible.sync="detailDialogVisible"
      width="80%"
      :modal="false"
      :close-on-click-modal="false"
    >
      <div v-if="selectedItem">
        <el-row :gutter="20" style="margin-bottom: 20px">
          <el-col :span="8">ID: {{ selectedItem.id }}</el-col>
          <el-col :span="8">产品: {{ selectedItem.target }}</el-col>
          <el-col :span="8">渠道: {{ selectedItem.src }}</el-col>
        </el-row>
        
        <el-table :data="callbackDetails" border size="mini" class="detail-table">
          <el-table-column prop="srcurl" label="产品回调链接" min-width="300">
            <template #default="{row}">
              <div class="text-cell">
                <!-- 添加el-tooltip用于显示完整内容 -->
                <el-tooltip :content="row.srcurl" placement="top" :disabled="!row.srcurl" popper-class="multi-poper">
                  <div class="text-content">
                    {{ row.srcurl }}
                  </div>
                </el-tooltip>
                <el-button 
                  size="mini" 
                  icon="el-icon-document-copy"
                  class="copy-btn"
                  @click="copyToClipboard(row.srcurl)"
                ></el-button>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column prop="callbackurl" label="渠道回调链接" min-width="300">
            <template #default="{row}">
              <div class="text-cell">
                <el-tooltip :content="row.callbackurl" placement="top" :disabled="!row.callbackurl" popper-class="multi-poper">
                  <div class="text-content">
                    {{ row.callbackurl }}
                  </div>
                </el-tooltip>
                <el-button 
                  size="mini" 
                  icon="el-icon-document-copy"
                  class="copy-btn"
                  @click="copyToClipboard(row.callbackurl)"
                ></el-button>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column prop="callbackrsp" label="渠道链接回复信息" min-width="250">
            <template #default="{row}">
              <el-tooltip :content="row.callbackrsp" placement="top" :disabled="!row.callbackrsp" popper-class="multi-poper">
                <div class="text-content">
                  {{ row.callbackrsp }}
                </div>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column prop="calltime" label="回调时间" min-width="250">
          </el-table-column>
        </el-table>
      </div>
      
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
    
    <!-- 回调操作弹窗 -->
    <el-dialog
      title="执行回调"
      :visible.sync="callbackDialogVisible"
      width="60%"
      :modal="false"
      :close-on-click-modal="false"
    >
      <div v-if="callbackItem">
        <div>
          <el-row :gutter="20" style="margin-bottom: 20px">
            <el-col :span="8">ID: {{ callbackItem.row.id }}</el-col>
            <el-col :span="8">产品: {{ callbackItem.row.target }}</el-col>
            <el-col :span="8">渠道: {{ callbackItem.row.src }}</el-col>
          </el-row>
        </div>
        <el-form :model="callbackForm" label-width="100px">
          <!-- 返回码选择 -->
          <el-form-item label="返回码：">
            <el-select v-model="callbackForm.responseCode" @change="callbackRescodeChange()" placeholder="选择返回码" style="width: 200px; margin-right: 15px">
              <el-option 
                v-for="code in responseCodes"
                :key="code.value"
                :label="code.label"
                :value="code.value"
              ></el-option>
            </el-select>
            
            <!-- 自定义输入框 -->
            <el-input 
              v-model="callbackForm.customValue" 
              placeholder="输入自定义值" 
              style="width: 200px"
              :disabled="callbackForm.responseCode !== 'custom'"
              @blur="callbackCustomResCodeInput()"
            ></el-input>
            <div>{{callbackForm.desc}}</div>
          </el-form-item>
          
          <!-- 确定回调按钮 -->
          <el-form-item>
            <el-button 
              type="primary" 
              @click="simulateCallback"
              :loading="callbackLoading"
            >确定回调</el-button>
          </el-form-item>
        </el-form>
        
        <!-- 流程节点展示 -->
        <div class="callback-process">
          <div class="process-header">回调流程</div>
          
          <!-- 第一步：回调链接 -->
          <div class="process-step">
            <div class="step-header">
              <span class="step-icon">1</span>
              <span class="step-title">回调链接</span>
            </div>
            <div class="step-content">
              <pre>{{ callbackItem.callbackUrl }}</pre>
            </div>
          </div>
          
          <!-- 第二步：回调响应 -->
          <div class="process-step">
            <div class="step-header">
              <span class="step-icon">2</span>
              <span class="step-title">回调响应</span>
            </div>
            <div class="step-content">
              <div v-if="callbackLoading" class="loading-response">
                <i class="el-icon-loading"></i> 请求中...
              </div>
              <div v-else-if="callbackError" class="error-response">
                请求失败: {{ callbackError }}
              </div>
              <div v-else-if="simulatedResponse" class="response-content">
                <el-collapse>
                  <el-collapse-item title="响应详情">
                    <pre>{{ simulatedResponse }}</pre>
                  </el-collapse-item>
                </el-collapse>
              </div>
              <div v-else class="empty-response">尚未执行回调</div>
            </div>
          </div>
        </div>
      </div>
      
      <template #footer>
        <el-button @click="callbackDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
  
</template>

<script>
import {GetDayLogs,GetProduct} from '../api/index'
import dayjs from 'dayjs'
import axios from 'axios'; // 添加axios导入
import { ocpxDomain } from '../config/domain'

export default {
  name: 'CallbackSearch',
  props: {
    // 外部传入的固定渠道
    externalChannel: {
      type: String,
      default: ''
    },
    // 新增：外部传入的固定产品
    externalProduct: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      // 搜索表单
      searchForm: {
        date: dayjs().format('YYYYMMDD'), // 默认今天
        method: 'recent',
        count: 10,
        callbackStatus: 'all',
        product: '',
        channel: ''
      },
      
      // 日期选择器配置
      pickerOptions: {
        disabledDate(time) {
          // 限制只能选择今天及以前的日期
          return time.getTime() > Date.now();
        }
      },
      
      // 表格数据
      tableData: [],
      curTableDataDate:'',
      
      // 分页信息
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      
      // 加载状态
      loading: false,
      
      // 详情弹窗控制
      detailDialogVisible: false,
      callbackDialogVisible: false,
      selectedItem: null,
      callbackItem: null,
      selectCallbackItemInfo:null,
      
      // 回调详情数据
      callbackDetails: [],
      
      // 回调操作表单
      callbackForm: {
        responseCode: '',
        customValue: ''
      },
      
      // 返回码选项
      responseCodes: [
        { label: '激活', value: 'activate' },
        { label: '注册', value: 'register' },
        { label: '次留', value: 'retain' },
        { label: '唤醒', value: 'awaken' },
        { label: '付费', value: 'paid' },
        { label: '自定义', value: 'custom' }
      ],
      
      // 模拟的回调响应
      simulatedResponse: '',
      // 回调加载状态
      callbackLoading: false,
      
      // 回调错误信息
      callbackError: '',
      
      // 固定渠道处理
      fixedChannel: this.externalChannel,
      fixedProduct: this.externalProduct
    };
  },
  watch: {
    // 监听外部传入的固定渠道变化
    externalChannel(newVal) {
      this.fixedChannel = newVal;
      if (newVal) {
        this.searchForm.channel = newVal;
      }
    },
    // 新增：监听外部传入的固定产品变化
    externalProduct(newVal) {
      this.fixedProduct = newVal;
      if (newVal) {
        this.searchForm.product = newVal;
      }
    }
  },
  mounted() {
    // 如果外部传入固定渠道，则设置到搜索表单
    if (this.fixedChannel) {
      this.searchForm.channel = this.fixedChannel;
    }
    
    // 新增：如果外部传入固定产品，则设置到搜索表单
    if (this.fixedProduct) {
      this.searchForm.product = this.fixedProduct;
    }
    
    // 初始化数据
    this.fetchData();
    this.injectTooltipStyles()
  },
  methods: {
    injectTooltipStyles() {
      // 确保样式插入到head中
      if (process.client) { // 仅限客户端
        const styleId = 'custom-tooltip-styles';
        if (!document.getElementById(styleId)) {
          const styleElement = document.createElement('style');
          styleElement.id = styleId;
          styleElement.innerHTML = `
            .custom-multiline-tooltip {
              max-width: 80vw !important;
              min-width: 300px !important;
              max-height: 80vh;
              overflow: auto;
              word-break: break-all !important;
              white-space: normal !important;
              line-height: 1.5 !important;
              padding: 12px !important;
              box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
              border: 1px solid #ebeef5;
            }
            .custom-multiline-tooltip .el-tooltip__popper .el-tooltip__arrow {
              display: none !important;
            }
          `;
          document.head.appendChild(styleElement);
        }
      }
    },
    // 获取表格数据
    fetchData() {
      this.loading = true;
      
      // 映射回调状态到API参数
      let filterValue = '';
      if (this.searchForm.callbackStatus === 'has') {
        filterValue = 'hasCallback';
      } else if (this.searchForm.callbackStatus === 'none') {
        filterValue = 'noCallback';
      }
      
      // 使用选择的日期
      const selectedDate = this.searchForm.date;
      
      GetDayLogs({
        date: selectedDate, // 使用选择的日期
        type: this.searchForm.method, 
        limit: this.searchForm.count, 
        filter: filterValue,
        src: this.searchForm.channel,
        target: this.searchForm.product
      }).then(res => {
        if (res.data == null) {
          this.loading = false;
          return;
        }
        const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
        this.tableData = res.data.slice(start, start + this.pagination.pageSize);
        this.curTableDataDate = selectedDate;
        this.pagination.total = res.data.length;
        this.loading = false;
      }).catch(err => {
        this.loading = false;
        this.$message.error('获取数据失败',err);
      });
    },
    
    // 处理搜索
    handleSearch() {
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    
    // 重置搜索
    resetSearch() {
      this.$refs.searchFormRef.resetFields();
      // 重置后重新设置日期为今天
      this.searchForm.date = dayjs().format('YYYYMMDD');
      this.searchForm.callbackStatus = 'all';
      if (this.fixedChannel) {
        this.searchForm.channel = this.fixedChannel;
      }
      this.handleSearch();
    },
    
    // 处理分页大小变化
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.fetchData();
    },
    
    // 处理页码变化
    handlePageChange(page) {
      this.pagination.currentPage = page;
      this.fetchData();
    },
    
    // 复制到剪贴板
    copyToClipboard(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      
      try {
        document.execCommand('copy');
        this.$message.success('复制成功');
      } catch (err) {
        this.$message.error('复制失败，浏览器不支持该操作');
      }
      
      document.body.removeChild(textArea);
    },
    
    // 显示回调详情
    showCallbackDetail(row) {
      this.selectedItem = row;
      
      try {
        // 解析回调详情数据
        this.callbackDetails = JSON.parse(row.callback);
      } catch (e) {
        console.error("解析回调详情失败:", e);
        this.callbackDetails = [];
        this.$message.error('解析回调详情数据失败');
      }
      
      this.detailDialogVisible = true;
    },

    callbackCustomResCodeInput()
    {
        var v = this.callbackForm.customValue.trim().replace(/\s+/g, "");
        if(v != "")
        {
            this.callbackForm.customValue = v
            this.callbackItem.callbackUrl = this.callbackItem.originUrl + this.callbackForm.customValue
        }
    },

    callbackRescodeChange()
    {
        if(this.callbackForm.responseCode != 'custom')
        {
            this.callbackForm.customValue = this.selectCallbackItemInfo[this.callbackForm.responseCode].value
            this.callbackItem.callbackUrl = this.callbackItem.originUrl + this.callbackForm.customValue

            if( this.selectCallbackItemInfo[this.callbackForm.responseCode].targetvalue != null)
            {
                this.callbackForm.desc = `产品配置 ${this.callbackForm.customValue} => ${this.selectCallbackItemInfo[this.callbackForm.responseCode].targetvalue}`
            }else{
                this.callbackForm.desc = ''
            }

        }else{
            this.callbackForm.desc = ''
            this.callbackForm.customValue = ''
        }

    },
    
    // 触发回调
    triggerCallback(row) {
      GetProduct(row.target).then(res=>{
        var transformParam = res.data.tar2macro["__TRANSFORMTYPE__"]
        var testCallbackUrl = `https://${ocpxDomain}/test/callback/comm/v2/?requestId=${row.id}&date=${this.curTableDataDate}&tar=${row.target}&${transformParam}=`
        //var testCallbackUrl = `http://127.0.0.1:10900/test/callback/comm/v2/?requestId=${row.id}&date=${this.curTableDataDate}&tar=${row.target}&${transformParam}=`

        this.callbackItem = {
            originUrl:testCallbackUrl,
            callbackUrl:testCallbackUrl,
            row
        };
        this.callbackForm.responseCode = '';
        this.callbackForm.customValue = '';
        this.simulatedResponse = '';
        this.callbackDialogVisible = true;
        this.callbackForm.desc = ''
        this.selectCallbackItemInfo = {
            activate:{
                value:1,
                targetvalue:null,
            },
            register:{
                value:2,
                targetvalue:null,
            },
            retain:{
                value:3,
                targetvalue:null,
            },
            awaken:{
                value:5,
                targetvalue:null,
            },
            paid:{
                value:8,
                targetvalue:null,
            }

        }
        if(res.data.tar2premac != null && res.data.tar2premac["__TRANSFORMTYPE__"] != null)
        {
            for(var key in res.data.tar2premac["__TRANSFORMTYPE__"])
            {
                var n = res.data.tar2premac["__TRANSFORMTYPE__"][key]
                if(n === "1")
                {
                    this.selectCallbackItemInfo.activate.value = key.toString()
                    this.selectCallbackItemInfo.activate.targetvalue = n
                }
                if(n === "2")
                {
                    this.selectCallbackItemInfo.register.value = key.toString()
                    this.selectCallbackItemInfo.register.targetvalue = n
                }
                if(n === "3")
                {
                    this.selectCallbackItemInfo.retain.value = key.toString()
                    this.selectCallbackItemInfo.retain.targetvalue = n
                }
                if(n === "5")
                {
                    this.selectCallbackItemInfo.awaken.value = key.toString()
                    this.selectCallbackItemInfo.awaken.targetvalue = n
                }
                if(n === "8")
                {
                    this.selectCallbackItemInfo.paid.value = key.toString()
                    this.selectCallbackItemInfo.paid.targetvalue = n
                }
            }
        }

      })
    
    },
    
    // 模拟回调
      simulateCallback() {
      if (!this.callbackForm.responseCode) {
        this.$message.warning('请选择返回码');
        return;
      }
      
      if (this.callbackForm.responseCode === 'custom' && !this.callbackForm.customValue) {
        this.$message.warning('请输入自定义值');
        return;
      }
      
      this.callbackLoading = true;
      this.callbackError = '';
      
      // 发送实际GET请求
      axios.get(this.callbackItem.callbackUrl)
        .then(response => {
          // 格式化为JSON字符串
          const responseData = {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            data: response.data
          };
          
          this.simulatedResponse = JSON.stringify(responseData, null, 2);
          this.$message.success('回调请求成功');
        })
        .catch(error => {
          let errorMessage = '请求失败';
          
          if (error.response) {
            // 服务器返回了错误响应
            errorMessage = `服务器错误: ${error.response.status} ${error.response.statusText}`;
          } else if (error.request) {
            // 请求已发送但无响应
            errorMessage = '请求无响应';
          } else {
            // 其他错误
            errorMessage = error.message || '未知错误';
          }
          
          this.callbackError = errorMessage;
          this.$message.error(errorMessage);
        })
        .finally(() => {
          this.callbackLoading = false;
        });
    },
    
    // 根据返回码获取事件名称
    getEventName(code) {
      const mapping = {
        'activate': '激活事件',
        'register': '注册事件',
        'retain': '次日留存',
        'awaken': '用户唤醒',
        'paid': '付费行为',
        'custom': '自定义事件'
      };
      return mapping[code] || '未知事件';
    }
  }
};
</script>

<style scoped>
.callback-search-container {
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
}

.text-cell {
  display: flex;
  align-items: center;
}

.text-ellipsis {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 详情表格中的文本样式 */
.text-content {
  white-space: normal;
  word-break: break-all;
  max-height: 60px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.copy-btn {
  margin-left: 8px;
  padding: 5px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

/* 回调流程样式 */
.callback-process {
  margin-top: 30px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.process-header {
  padding: 10px 15px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  font-weight: bold;
}

.process-step {
  padding: 15px;
  border-bottom: 1px solid #ebeef5;
}

.process-step:last-child {
  border-bottom: none;
}

.step-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.step-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #409eff;
  color: white;
  font-size: 14px;
  margin-right: 10px;
}

.step-title {
  font-weight: bold;
}

.step-content {
  padding: 10px;
  background-color: #fafafa;
  border-radius: 4px;
  max-height: 200px;
  overflow: auto;
}

.step-content pre {
  margin: 0;
  font-family: Consolas, Monaco, monospace;
  white-space: pre-wrap;
}

.empty-response {
  color: #909399;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

/* 详情表格样式 */
.detail-table .el-table__body-wrapper {
  max-height: 400px;
  overflow-y: auto;
}

.detail-table .el-table__cell {
  padding: 8px 0;
}

/* 新增紧凑布局样式 */
.compact-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.compact-item {
  margin-right: 30px !important; /* 减少表单项间距 */
  margin-bottom: 30px !important;
}

/* 标签文字样式调整 */
.compact-item ::v-deep .el-form-item__label {
  padding-right: 5px !important;
  font-size: 13px !important;
}

</style>

<style>
.el-tooltip__popper.multi-poper {
  max-width: 40vw; /* 最大宽度为视口的80% */
  word-break: break-all; /* 强制断行 */
  white-space: normal; /* 覆盖默认的nowrap设置 */
  padding: 5px; /* 增加内边距 */
  line-height: 1.0; /* 更好的行高 */
  text-align: left; /* 左对齐文本 */
  max-height: 60vh; /* 最大高度 */
  overflow: auto; /* 添加滚动条 */
}
</style>